import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort, faSortUp, faSortDown, faSpinner, faCheck, faTimes, faEye,
  faFilter, faFileExport, faSearch, faBug, faRedo, faCrosshairs,
  faCopy, faExclamationTriangle, faChevronDown, faArrowDown
} from '@fortawesome/free-solid-svg-icons';
import intruderService from '../../services/intruderServiceFrontend';
import httpExchangeService from '../../services/httpExchangeService';
import webFindingService from '../../services/webFindingService';
import ContextMenu from './ContextMenu';
import { toast } from 'react-toastify';

const IntruderResults = ({ attack, grepMatch, grepExtract, onViewResult, onSendToRepeater, onSendToIntruder, webTarget, isDarkMode }) => {
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [selectedResultId, setSelectedResultId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [filters, setFilters] = useState({
    statusCodes: '',
    minLength: '',
    maxLength: '',
    minTime: '',
    maxTime: '',
    anomalyOnly: false,
  });
  const pollRef = useRef(null);
  const tableRef = useRef(null);
  const searchTimer = useRef(null);

  const buildParams = useCallback((pageNum = 1, loadAll = false) => {
    const isFinished = attack?.status === 'completed' || attack?.status === 'failed' || attack?.status === 'cancelled';
    const params = { page: pageNum, limit: loadAll || isFinished ? 10000 : 200 };
    if (sortField) {
      params.sortField = sortField;
      params.sortDir = sortDir;
    }
    if (searchApplied) params.search = searchApplied;
    if (filters.statusCodes) params.statusCodes = filters.statusCodes;
    if (filters.minLength) params.minLength = filters.minLength;
    if (filters.maxLength) params.maxLength = filters.maxLength;
    if (filters.minTime) params.minTime = filters.minTime;
    if (filters.maxTime) params.maxTime = filters.maxTime;
    if (filters.anomalyOnly) params.anomalyOnly = 'true';
    return params;
  }, [sortField, sortDir, searchApplied, filters, attack?.status]);

  const loadResults = useCallback(async (append = false) => {
    if (!attack?._id) return;
    const pageNum = append ? page + 1 : 1;
    try {
      if (append) setLoadingMore(true); else setLoading(true);
      const data = await intruderService.getResults(attack._id, buildParams(pageNum));
      if (append) {
        setResults(prev => [...prev, ...(data.results || [])]);
      } else {
        setResults(data.results || []);
      }
      setTotal(data.total || 0);
      setHasMore(data.hasMore || false);
      setPage(pageNum);
    } catch {
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [attack?._id, buildParams, page]);

  useEffect(() => {
    setPage(1);
    loadResults(false);
  }, [attack?._id, sortField, sortDir, searchApplied, filters]);

  // Poll while running
  useEffect(() => {
    if (attack?.status === 'running') {
      pollRef.current = setInterval(async () => {
        try {
          const data = await intruderService.getResults(attack._id, buildParams(1));
          setResults(data.results || []);
          setTotal(data.total || 0);
          setHasMore(data.hasMore || false);
          setPage(1);
        } catch {}
      }, 2000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [attack?.status, attack?._id, buildParams]);

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortField(null); setSortDir('asc'); }
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setSearchApplied(searchText);
    }
  };

  const handleSearchClear = () => {
    setSearchText('');
    setSearchApplied('');
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({ statusCodes: '', minLength: '', maxLength: '', minTime: '', maxTime: '', anomalyOnly: false });
    setSearchText('');
    setSearchApplied('');
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.statusCodes) count++;
    if (filters.minLength || filters.maxLength) count++;
    if (filters.minTime || filters.maxTime) count++;
    if (filters.anomalyOnly) count++;
    if (searchApplied) count++;
    return count;
  }, [filters, searchApplied]);

  const sortIcon = (field) => {
    if (sortField !== field) return faSort;
    return sortDir === 'asc' ? faSortUp : faSortDown;
  };

  const statusColor = (code) => {
    if (!code) return isDarkMode ? 'text-gray-500' : 'text-gray-400';
    if (code < 300) return 'text-green-500';
    if (code < 400) return 'text-blue-400';
    if (code < 500) return 'text-yellow-500';
    return 'text-red-500';
  };

  // Compute baseline from modal stats (median length and most common status)
  const { baselineLength, baselineStatus } = useMemo(() => {
    if (results.length < 3) return { baselineLength: null, baselineStatus: null };
    const lengths = results.map(r => r.response?.contentLength).filter(l => l != null).sort((a, b) => a - b);
    const statuses = {};
    results.forEach(r => {
      const s = r.response?.statusCode;
      if (s) statuses[s] = (statuses[s] || 0) + 1;
    });
    const medianLen = lengths.length > 0 ? lengths[Math.floor(lengths.length / 2)] : null;
    const topStatus = Object.entries(statuses).sort((a, b) => b[1] - a[1])[0]?.[0];
    return { baselineLength: medianLen, baselineStatus: topStatus ? parseInt(topStatus) : null };
  }, [results]);

  const isAnomaly = useCallback((result) => {
    const status = result.response?.statusCode;
    const length = result.response?.contentLength;
    if (baselineStatus != null && status !== baselineStatus) return true;
    if (baselineLength != null && length != null && Math.abs(length - baselineLength) > baselineLength * 0.15) return true;
    return false;
  }, [baselineLength, baselineStatus]);

  const anomalyCount = useMemo(() => results.filter(isAnomaly).length, [results, isAnomaly]);

  // Context menu
  const handleContextMenu = (e, result) => {
    e.preventDefault();
    e.stopPropagation();

    const buildCurl = () => {
      const method = result.request?.method || 'GET';
      const url = result.request?.url || '';
      let cmd = `curl -X ${method} '${url}'`;
      return cmd;
    };

    setContextMenu({
      position: { x: e.clientX, y: e.clientY },
      items: [
        {
          label: 'View Response',
          icon: faEye,
          onClick: () => { setContextMenu(null); handleViewResult(result); }
        },
        { divider: true },
        {
          label: 'Send to Repeater',
          icon: faRedo,
          onClick: async () => {
            setContextMenu(null);
            try {
              const full = await httpExchangeService.getById(result._id);
              onSendToRepeater?.(full);
            } catch { toast.error('Failed to load exchange'); }
          }
        },
        {
          label: 'Send to Intruder (new)',
          icon: faCrosshairs,
          onClick: async () => {
            setContextMenu(null);
            try {
              const full = await httpExchangeService.getById(result._id);
              onSendToIntruder?.(full);
            } catch { toast.error('Failed to load exchange'); }
          }
        },
        { divider: true },
        {
          label: 'Add to Findings',
          icon: faBug,
          onClick: () => { setContextMenu(null); handleAddToFindings(result); }
        },
        { divider: true },
        {
          label: 'Copy URL',
          icon: faCopy,
          onClick: () => {
            setContextMenu(null);
            navigator.clipboard.writeText(result.request?.url || '').catch(() => {});
            toast.success('URL copied');
          }
        },
        {
          label: 'Copy as curl',
          icon: faCopy,
          onClick: () => {
            setContextMenu(null);
            navigator.clipboard.writeText(buildCurl()).catch(() => {});
            toast.success('curl command copied');
          }
        },
      ]
    });
  };

  const handleAddToFindings = async (result) => {
    const payload = result.mutation?.mutatedValue || 'unknown';
    const status = result.response?.statusCode || '?';
    const length = result.response?.contentLength || '?';
    try {
      await webFindingService.create({
        webTarget: webTarget?._id || attack?.webTarget,
        project: webTarget?.project || attack?.project,
        title: `Intruder: Anomalous response for payload "${payload}"`,
        description: `Intruder attack "${attack?.name || 'unknown'}" returned status ${status} (${length} bytes) for payload "${payload}". This response differs from the baseline and may indicate a vulnerability.`,
        severity: 'medium',
        confidence: 'tentative',
        endpoint: {
          method: result.request?.method,
          url: result.request?.url,
        },
        parameter: {
          name: result.mutation?.parameterName,
          value: payload,
        },
        httpExchanges: [result._id],
        evidence: [{
          type: 'request_response',
          title: 'Intruder result',
          content: `Status: ${status}, Length: ${length}, Payload: ${payload}`,
          exchangeId: result._id
        }]
      });
      toast.success('Finding created');
    } catch (err) {
      toast.error('Failed to create finding: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleViewResult = async (result) => {
    setSelectedResultId(result._id);
    try {
      const full = await httpExchangeService.getById(result._id);
      onViewResult?.(full);
    } catch {
      onViewResult?.(result);
    }
  };

  const handleExport = () => {
    if (!attack?._id) return;
    intruderService.exportResults(attack._id);
  };

  const handleLoadMore = () => {
    loadResults(true);
  };

  const handleLoadAll = async () => {
    if (!attack?._id) return;
    try {
      setLoading(true);
      const data = await intruderService.getResults(attack._id, buildParams(1, true));
      setResults(data.results || []);
      setTotal(data.total || 0);
      setHasMore(data.hasMore || false);
      setPage(1);
    } catch {} finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className={`flex items-center justify-between px-3 py-2 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center gap-3">
          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {total} results
            {results.length < total && ` (showing ${results.length})`}
          </span>
          {attack?.status === 'running' && (
            <span className="flex items-center gap-1 text-[10px] text-blue-400">
              <FontAwesomeIcon icon={faSpinner} spin />
              Running... {attack.progress?.completedRequests || 0}/{attack.progress?.totalRequests || 0}
            </span>
          )}
          {attack?.status === 'completed' && (
            <span className="flex items-center gap-1 text-[10px] text-green-400">
              <FontAwesomeIcon icon={faCheck} /> Completed
            </span>
          )}
          {attack?.status === 'failed' && (
            <span className="flex items-center gap-1 text-[10px] text-red-400">
              <FontAwesomeIcon icon={faTimes} /> Failed
            </span>
          )}
          {anomalyCount > 0 && (
            <span className="flex items-center gap-1 text-[10px] text-yellow-400" title="Responses that differ from the baseline">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              {anomalyCount} anomalies
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="relative">
            <input
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search in response..."
              className={`text-xs pl-6 pr-6 py-1 rounded border w-44 ${
                isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'
              }`}
            />
            <FontAwesomeIcon icon={faSearch} className={`absolute left-2 top-1/2 -translate-y-1/2 text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            {searchText && (
              <button
                onClick={handleSearchClear}
                className={`absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] px-0.5 ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
              activeFilterCount > 0
                ? 'bg-orange-600/20 text-orange-400 border border-orange-500/30'
                : isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
            }`}
            title="Filter results"
          >
            <FontAwesomeIcon icon={faFilter} className="text-[10px]" />
            Filter
            {activeFilterCount > 0 && <span className="bg-orange-500 text-white rounded-full w-3.5 h-3.5 flex items-center justify-center text-[8px]">{activeFilterCount}</span>}
          </button>
          {/* Export */}
          <button
            onClick={handleExport}
            disabled={results.length === 0}
            className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
              isDarkMode ? 'text-gray-400 hover:bg-gray-700 disabled:opacity-30' : 'text-gray-500 hover:bg-gray-100 disabled:opacity-30'
            }`}
            title="Export as CSV"
          >
            <FontAwesomeIcon icon={faFileExport} className="text-[10px]" />
            Export
          </button>
        </div>
      </div>

      {/* Filter bar */}
      {showFilters && (
        <div className={`flex flex-wrap items-center gap-3 px-3 py-2 border-b text-xs ${isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50/50'}`}>
          <div className="flex items-center gap-1.5">
            <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Status:</span>
            <input
              value={filters.statusCodes}
              onChange={e => handleFilterChange('statusCodes', e.target.value)}
              placeholder="200,302,500"
              className={`w-28 px-1.5 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Length:</span>
            <input
              value={filters.minLength}
              onChange={e => handleFilterChange('minLength', e.target.value)}
              placeholder="min"
              className={`w-16 px-1.5 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
            />
            <span className={isDarkMode ? 'text-gray-600' : 'text-gray-400'}>—</span>
            <input
              value={filters.maxLength}
              onChange={e => handleFilterChange('maxLength', e.target.value)}
              placeholder="max"
              className={`w-16 px-1.5 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Time:</span>
            <input
              value={filters.minTime}
              onChange={e => handleFilterChange('minTime', e.target.value)}
              placeholder="min ms"
              className={`w-16 px-1.5 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
            />
            <span className={isDarkMode ? 'text-gray-600' : 'text-gray-400'}>—</span>
            <input
              value={filters.maxTime}
              onChange={e => handleFilterChange('maxTime', e.target.value)}
              placeholder="max ms"
              className={`w-16 px-1.5 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
            />
          </div>
          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.anomalyOnly}
              onChange={e => handleFilterChange('anomalyOnly', e.target.checked)}
              className="w-3 h-3 rounded accent-orange-500"
            />
            <span className={`${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>Anomalies only</span>
          </label>
          {activeFilterCount > 0 && (
            <button
              onClick={handleClearFilters}
              className="text-[10px] px-2 py-0.5 rounded text-red-400 hover:bg-red-900/20"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Results table */}
      <div className="flex-1 overflow-auto" ref={tableRef}>
        <table className="w-full text-xs">
          <thead className={`sticky top-0 z-10 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <tr className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
              <th className="text-left px-2 py-1.5 w-12">#</th>
              <th className="text-left px-2 py-1.5 cursor-pointer select-none" onClick={() => handleSort('payload')}>
                <span className="flex items-center gap-1">Payload <FontAwesomeIcon icon={sortIcon('payload')} className="text-[8px] opacity-60" /></span>
              </th>
              <th className="text-left px-2 py-1.5 w-16 cursor-pointer select-none" onClick={() => handleSort('status')}>
                <span className="flex items-center gap-1">Status <FontAwesomeIcon icon={sortIcon('status')} className="text-[8px] opacity-60" /></span>
              </th>
              <th className="text-left px-2 py-1.5 w-20 cursor-pointer select-none" onClick={() => handleSort('length')}>
                <span className="flex items-center gap-1">Length <FontAwesomeIcon icon={sortIcon('length')} className="text-[8px] opacity-60" /></span>
              </th>
              <th className="text-left px-2 py-1.5 w-16 cursor-pointer select-none" onClick={() => handleSort('time')}>
                <span className="flex items-center gap-1">Time <FontAwesomeIcon icon={sortIcon('time')} className="text-[8px] opacity-60" /></span>
              </th>
              {(grepMatch || []).map((rule, i) => (
                <th key={`gm-${i}`} className="text-left px-2 py-1.5 w-16">
                  <span className="text-[10px]">{rule.name || `Match ${i + 1}`}</span>
                </th>
              ))}
              {(grepExtract || []).map((rule, i) => (
                <th key={`ge-${i}`} className="text-left px-2 py-1.5">
                  <span className="text-[10px]">{rule.name || `Extract ${i + 1}`}</span>
                </th>
              ))}
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {results.map((r, i) => {
              const anomaly = isAnomaly(r);
              return (
                <tr
                  key={r._id || i}
                  onClick={() => handleViewResult(r)}
                  onContextMenu={(e) => handleContextMenu(e, r)}
                  className={`cursor-pointer border-b ${
                    selectedResultId === r._id
                      ? (isDarkMode ? 'bg-orange-900/20' : 'bg-orange-50')
                      : anomaly
                        ? (isDarkMode ? 'bg-yellow-900/10 border-gray-800 hover:bg-yellow-900/20' : 'bg-yellow-50 border-gray-100 hover:bg-yellow-100')
                        : (isDarkMode ? 'border-gray-800 hover:bg-gray-800/50' : 'border-gray-100 hover:bg-gray-50')
                  }`}
                >
                  <td className={`px-2 py-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {i + 1}
                    {anomaly && <FontAwesomeIcon icon={faExclamationTriangle} className="ml-1 text-yellow-500 text-[8px]" />}
                  </td>
                  <td className={`px-2 py-1 font-mono truncate max-w-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {r.mutation?.mutatedValue || '-'}
                  </td>
                  <td className="px-2 py-1">
                    <span className={`font-mono font-bold ${statusColor(r.response?.statusCode)}`}>
                      {r.response?.statusCode || '-'}
                    </span>
                  </td>
                  <td className={`px-2 py-1 font-mono ${
                    anomaly && baselineLength != null && r.response?.contentLength != null && Math.abs(r.response.contentLength - baselineLength) > baselineLength * 0.15
                      ? 'text-yellow-400 font-bold' : (isDarkMode ? 'text-gray-400' : 'text-gray-500')
                  }`}>
                    {r.response?.contentLength != null ? r.response.contentLength : '-'}
                  </td>
                  <td className={`px-2 py-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {r.response?.timing?.totalMs != null ? `${r.response.timing.totalMs}ms` : '-'}
                  </td>
                  {(grepMatch || []).map((rule, gi) => {
                    const matched = r.grepResults?.matches?.[gi]?.matched ??
                      (r.tags || []).includes(`grep:${rule.name}`);
                    return (
                      <td key={`gm-${gi}`} className="px-2 py-1 text-center">
                        {matched ? (
                          <FontAwesomeIcon icon={faCheck} className="text-green-400 text-[10px]" />
                        ) : (
                          <FontAwesomeIcon icon={faTimes} className="text-gray-600 text-[10px]" />
                        )}
                      </td>
                    );
                  })}
                  {(grepExtract || []).map((rule, gi) => (
                    <td key={`ge-${gi}`} className={`px-2 py-1 font-mono text-[10px] truncate max-w-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {r.grepResults?.extracts?.[gi]?.value || '-'}
                    </td>
                  ))}
                  <td className="px-2 py-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleViewResult(r); }}
                      className={`p-1 rounded ${isDarkMode ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                      title="View details"
                    >
                      <FontAwesomeIcon icon={faEye} className="text-[10px]" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Load more */}
        {hasMore && !loading && (
          <div className="flex justify-center gap-2 py-3">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className={`px-4 py-1.5 rounded text-xs flex items-center gap-1.5 ${
                isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {loadingMore ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faArrowDown} />}
              Load more ({results.length} / {total})
            </button>
            <button
              onClick={handleLoadAll}
              disabled={loadingMore}
              className={`px-4 py-1.5 rounded text-xs flex items-center gap-1.5 ${
                isDarkMode ? 'bg-orange-700 text-orange-100 hover:bg-orange-600' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
              }`}
            >
              Load all {total}
            </button>
          </div>
        )}

        {results.length === 0 && !loading && (
          <div className={`text-center py-8 text-xs ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
            {attack?.status === 'configured' ? 'Start the attack to see results.' : 'No results yet.'}
          </div>
        )}
      </div>

      {/* Context menu */}
      {contextMenu && (
        <ContextMenu
          items={contextMenu.items}
          position={contextMenu.position}
          onClose={() => setContextMenu(null)}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
};

export default IntruderResults;
